import React, { useEffect, useState } from 'react';

/* MUI */
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  Collapse,
  Typography,
} from '@mui/material';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import SensorOccupiedIcon from '@mui/icons-material/SensorOccupied';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
/* Gen Types */
import {
  OccupancySensor,
  useOccupancySensorDetailQuery,
  useOccupancySensorDetailUpdateSubscription,
} from '../../../../types/generated-types';
/* Shared */
import { DeviceInfo, DeviceInformation } from '../shared-ui/device-info';
import {
  DeviceAlerts,
  ToggleAlertDetailsButton,
} from '../shared-ui/alerts-info';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import moment from 'moment';
import {
  useDeviceHasAlerts,
  useDeviceAndSourceAreOnline,
} from '../../../system/AlertsManager';

interface OccupancySensorComponentProps {
  deviceId: string;
}

function OccupancySensorIcon(device: Partial<OccupancySensor>) {
  const status = device?.occupancy?.value;

  if (status !== 'On') {
    return <PersonOffOutlinedIcon sx={{ color: 'rgba(0, 0, 0, 0.63)' }} />;
  } else {
    return <SensorOccupiedIcon sx={{ color: '#80BB52' }} />;
  }
}

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: '-8px',
  marginRight: '-8px',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export function OccupancySensorComponent(props: OccupancySensorComponentProps) {
  const { deviceId } = props;
  const { data, loading, error } = useOccupancySensorDetailQuery({
    variables: { id: deviceId },
  });
  const [device, setDevice] = useState<Partial<OccupancySensor>>();
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  useOccupancySensorDetailUpdateSubscription({
    variables: { ids: [deviceId] },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  const hasAlerts = useDeviceHasAlerts(device);
  const isOnline = useDeviceAndSourceAreOnline(device);

  useEffect(() => {
    if (data?.occupancySensorById) {
      setDevice(data.occupancySensorById as Partial<OccupancySensor>);
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const panelInfo: DeviceInformation = {
        Zone: device.zone?.name ?? 'Unnamed',
        'Zone ID': device.zone?._id ?? 'Unidentified',
        Source: device.source?.name ?? 'Unnamed source',
      };

      const baseDevicesInfo: DeviceInformation = {
        'Device ID': device.deviceId ?? device._id ?? 'Unidentified',
        Name: device.name ?? 'Unnamed',
        Type: device.typeDisplayName ?? 'Unknown',
        firmwareInfo: {
          Model:
            device.meta?.model || device.meta?.modelNumber || 'Unknown model',
          Version: device.meta?.version || 'Unknown version',
        },
        'Last Updated': moment(device.timestamp).fromNow(),
      };

      const devicesInfo: DeviceInformation[] = [baseDevicesInfo];

      setDeviceInfo([panelInfo, ...devicesInfo]);
    }
  }, [device, isOnline]);

  const name = device?.name || 'null';
  const [expanded, setExpanded] = React.useState(false);
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };
  const statusText = isOnline
    ? device?.occupancy?.value === 'On'
      ? 'Occupied'
      : 'Unoccupied'
    : 'Offline';
  const responsiveText = name + ': ' + statusText;

  return error ? (
    <div>Error</div>
  ) : loading ? (
    <DeviceLoadingSkeleton />
  ) : device ? (
    <Card
      sx={{
        backgroundColor: '#ebf7f6',
        boxShadow: 1,
        p: 0,
        width: '100%',
        border: hasAlerts ? '2px solid red' : '',
        borderRadius: '4px',
        marginTop: '4px',
      }}
    >
      <CardHeader
        sx={{
          paddingTop: '0px',
          paddingLeft: '10px',
          paddingRight: '10ox',
          paddingBottom: '0px',
          border: '1px solid rgba(0,0,0, 0.1)',
        }}
        title={
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ flex: '0 0 auto', flexDirection: 'column' }}>
              <Box
                sx={{
                  padding: '0px',
                }}
              >
                <div
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="h5"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      paddingTop: '0px',
                      color: isOnline ? 'black' : 'red',
                    }}
                  >
                    {responsiveText}
                  </Typography>
                </div>
              </Box>
            </div>
          </div>
        }
        avatar={
          <Avatar
            sx={{
              backgroundColor: 'transparent',
              padding: '0px',
              margin: '0px 2px 0px 2px',
            }}
          >
            {hasAlerts ? (
              <ToggleAlertDetailsButton
                device={device}
                expand={alertsExpanded}
                onClick={(event) => {
                  event.preventDefault();
                  setAlertsExpanded(!alertsExpanded);
                }}
                buttonLabel="Show alerts"
              />
            ) : (
              <OccupancySensorIcon {...device} />
            )}
          </Avatar>
        }
        action={
          <Box>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                padding: '0',
              }}
            >
              <ExpandMore
                expand={expanded}
                onClick={handleExpandClick}
                aria-expanded={expanded}
                aria-label="show more"
                sx={{ padding: '0px', margin: '0px' }}
              >
                <ExpandMoreIcon style={{ padding: '0px', margin: '0px' }} />
              </ExpandMore>
            </div>
          </Box>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <DeviceInfo infoEntries={deviceInfo} />
      </Collapse>
      <DeviceAlerts device={device} alertsExpanded={alertsExpanded} />
    </Card>
  ) : null;
}
